import styled from '@emotion/styled';
import { forwardRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { tokens, Text, Button } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { FloatingPanel } from '@hotjar/activation-toolkit';
import { useCurrentSite } from '@hotjar/site';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { PLAYBACKS } from '@hotjar/webapp/constants/routes';
import PopcornBanner from './assets/popcorn2.jpg';
import { useRecordingsPopup } from './useRecordingsPopup';

const Banner = styled.div`
  margin: 0 ${tokens.spacer.x1} ${tokens.spacer.x2} ${tokens.spacer.x1};
  height: 120px;
  background-position: center;
  background-size: cover;
  transition: background-image 500ms ease;
`;

const Content = styled.div`
  display: flex;
  margin: 0 ${tokens.spacer.x1};
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const UI_POSITION = 'first recording popup';
const COLLAPSED_KEY = 'firstRecordingCollapsed';

export const RecordingsPopup = forwardRef<HTMLElement, unknown>(
  (_props, ref): JSX.Element | null => {
    const track = useTrack();
    const { dismiss, isVisible } = useRecordingsPopup();
    const { data: currentSite } = useCurrentSite();
    const { t } = useTranslation();
    const { click: trackClick } = useTrack();
    const siteUrl = currentSite?.url;

    const handleOnDismiss = () => {
      trackClick('skip', UI_POSITION, {});
      dismiss();
    };
    const onToggle = (isCollapsed: boolean) => {
      trackClick(isCollapsed ? 'Collapse' : 'Expand', UI_POSITION, {});
    };

    const onCTAClick = () => {
      trackClick('go to recording', UI_POSITION, {});

      dismiss(true);
    };

    useEffect(() => {
      track.viewElement('First Recording', 'FloatingSuggestions', {});
    }, [track]);

    if (!isVisible) {
      return null;
    }

    return (
      <FloatingPanel
        collapsedStateKey={COLLAPSED_KEY}
        remainingTasks={1}
        title={t('activation:insights-notifications.recordings.popup-title')}
        onDismiss={handleOnDismiss}
        onToggle={onToggle}
        ref={ref}
      >
        <Banner
          aria-hidden
          data-testid="recordings-popup-section-banner"
          style={{ backgroundImage: `url('${PopcornBanner}')` }}
        />

        <Content>
          <Text css={{ marginBottom: tokens.spacer.x1 }} as="h4" textType="header5">
            {t('activation:insights-notifications.recordings.title')}
          </Text>
          <Text
            textColor="text_light"
            css={{ marginBottom: tokens.spacer.x2 }}
            as="p"
            textType="body2"
          >
            <Trans i18nKey="activation:insights-notifications.recordings.text">
              You’ve captured your first session on <strong>{{ siteUrl }}</strong> and the recording
              is ready for watching.
            </Trans>
          </Text>
          <ButtonWrapper>
            {currentSite?.id ? (
              <Button
                as="a"
                href={getRoute(PLAYBACKS.LIST.path, { siteId: currentSite.id })}
                onClick={onCTAClick}
                css={{ marginRight: tokens.spacer.x2 }}
                kind="primary"
              >
                {t('activation:insights-notifications.recordings.cta')}
              </Button>
            ) : null}

            <Button onClick={handleOnDismiss} kind="secondary">
              {t('activation:insights-notifications.recordings.skip')}
            </Button>
          </ButtonWrapper>
        </Content>
      </FloatingPanel>
    );
  },
);
